import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { AnalyticsOverview } from '@proref/types';
import { AnimatedNumber } from '../shared/AnimatedNumber';
import { Skeleton } from '../shared/Skeleton';

// The four KPI headlines from the design, all federation-scoped and live. Each carries a real
// period-over-period comparison (the service returns the prior window), so the sub-line states an
// honest delta rather than the mockup's placeholder copy.

interface AnalyticsKpiCardsProps {
  kpis: AnalyticsOverview['kpis'] | undefined;
  accuracyTarget: number;
  loading: boolean;
}

export function AnalyticsKpiCards({ kpis, accuracyTarget, loading }: AnalyticsKpiCardsProps) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <KpiTile
        label={t('admin.analytics.kpi.activeReferees')}
        loading={loading}
        value={<AnimatedNumber value={kpis?.active_referees.value ?? 0} />}
        foot={countDelta(kpis?.active_referees.value, kpis?.active_referees.previous, t('admin.analytics.kpi.vsPrev30Days'))}
      />
      <KpiTile
        label={t('admin.analytics.kpi.dailyClipCompletion')}
        loading={loading}
        value={<Pct rate={kpis?.daily_completion.value ?? 0} />}
        foot={rateDelta(t, kpis?.daily_completion.value, kpis?.daily_completion.previous, t('admin.analytics.kpi.vsLastWeek'))}
      />
      <KpiTile
        label={t('admin.analytics.kpi.avgRuleAccuracy')}
        loading={loading}
        value={<Pct rate={kpis?.avg_accuracy.value ?? 0} />}
        foot={
          <span className="text-muted">
            {t('admin.analytics.kpi.targetPercent', { percent: Math.round(accuracyTarget * 100) })}
          </span>
        }
      />
      <KpiTile
        label={t('admin.analytics.kpi.discussionReplies')}
        loading={loading}
        value={<AnimatedNumber value={kpis?.discussion_replies.value ?? 0} />}
        foot={countDelta(
          kpis?.discussion_replies.value,
          kpis?.discussion_replies.previous,
          t('admin.analytics.kpi.thisWeekVsLast'),
        )}
      />
    </div>
  );
}

interface KpiTileProps {
  label: string;
  value: ReactNode;
  foot: ReactNode;
  loading: boolean;
}

function KpiTile({ label, value, foot, loading }: KpiTileProps) {
  return (
    <div className="rounded border border-line bg-surface p-5 shadow-1">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted">{label}</div>
      <div className="mt-2 font-display text-4xl leading-none text-ink-3">
        {loading ? <Skeleton className="h-9 w-20" /> : value}
      </div>
      <div className="mt-2 text-[11px]">{loading ? <Skeleton className="h-3 w-24" /> : foot}</div>
    </div>
  );
}

function Pct({ rate }: { rate: number }) {
  return (
    <>
      <AnimatedNumber value={Math.round(rate * 100)} />
      <span className="ml-0.5 align-baseline text-lg text-muted">%</span>
    </>
  );
}

// Delta line for a count metric: "↑ 312 vs prev 30 days". No prior data → just the context label.
function countDelta(value: number | undefined, previous: number | null | undefined, label: string): ReactNode {
  if (value == null || previous == null) return <span className="text-muted">{label}</span>;
  const diff = value - previous;
  return <TrendLine diff={diff} text={`${diff >= 0 ? '+' : ''}${diff} ${label}`} />;
}

// Delta line for a rate metric: "↑ vs 61% last week" — shows the prior percentage for context.
function rateDelta(t: TFunction, value: number | undefined, previous: number | null | undefined, label: string): ReactNode {
  if (value == null || previous == null) return <span className="text-muted">{label}</span>;
  const diff = value - previous;
  return (
    <TrendLine
      diff={diff}
      text={`${label} · ${t('admin.analytics.kpi.beforePercent', { percent: Math.round(previous * 100) })}`}
    />
  );
}

function TrendLine({ diff, text }: { diff: number; text: string }) {
  // Flat (no change) reads as neutral, not a false win/loss.
  const tone = diff > 0 ? 'text-success' : diff < 0 ? 'text-danger' : 'text-muted';
  const arrow = diff > 0 ? '↑' : diff < 0 ? '↓' : '→';
  return (
    <span className={tone}>
      <span aria-hidden="true">{arrow} </span>
      {text}
    </span>
  );
}
